
import React, {
  useEffect,
  useMemo,
  useState
} from "react";

import { motion } from "framer-motion";

import {
  Layout,
  Menu,
  Drawer,
  Button,
  Typography,
  Badge,
  Dropdown
} from "antd";

import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DashboardOutlined,
  UserOutlined,
  ReadOutlined,
  CalendarOutlined,
  FileTextOutlined,
  NotificationOutlined,
  LogoutOutlined,
  WalletOutlined,
  ArrowRightOutlined
} from "@ant-design/icons";

import {
  Box,
  Grid,
  Avatar
} from "@mui/material";

import {
  FaGraduationCap,
  FaBookReader,
  FaCalendarAlt,
  FaUserGraduate,
  FaStar
} from "react-icons/fa";

import Swal from "sweetalert2";
import api from "../api/api";

import ParentAndStudentProfile from "./ParentAndStudentProfile";
import AssignmentView from "../pages/Teachers/AssignmentView";
import BioLinkProfile from "./BioLinkProfile";
import TransActionPerStudent from "../SchoolDashApp/DailyTransaction/TransActionPerStudent";
import ParentsStudentGrades from "../pages/Students/ParentsStudentGrades";

import defaultAvatar from "./maleee.jpg";
import logo from "./zpa.png";
import dashboardBanner from "./teacherss.jpg";

import { useNavigate } from "react-router-dom";

const {
  Header,
  Sider,
  Content
} = Layout;

const {
  Title,
  Text
} = Typography;


// ======================================================
// ZION PRAISE ACADEMY COLOR PALETTE
// ======================================================

const COLORS = {

  navy: "#031B35",
  deepNavy: "#021426",
  navyBlue: "#062A4B",
  blue: "#063A6B",
  black: "#071018",

  gold: "#F6C944",
  goldLight: "#FFE58A",
  yellow: "#EFA81C",
  orange: "#C0750D",
  antique: "#CBA454",

  green: "#4FAE22",
  sky: "#0F6DAB",

  white: "#FFFFFF",
  softWhite: "#F7F9FC",
  gray: "#C4C7C7",

  page: "#F4F7F9",
  pureWhite: "#FFFFFF",
  text: "#142536",
  muted: "#6B7280",
  border: "#E0E7EC",
  soft: "#F7F9FA"

};


// ======================================================
// MAIN COMPONENT
// ======================================================

const StudentDashboard = () => {

  const navigate = useNavigate();

  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);

  const [activePage, setActivePage] = useState("dashboard");

  const [showPayments, setShowPayments] = useState(false);

  const [user, setUser] = useState(null);

  const [loading, setLoading] = useState(true);

  const [children, setChildren] = useState([]);

  const [selectedChild, setSelectedChild] = useState(null);

  const [showGrades, setShowGrades] = useState(false);

  const [notifications, setNotifications] = useState([]);

  const [unreadCount, setUnreadCount] = useState(0);

  const [notificationOpen, setNotificationOpen] = useState(false);

  const [clickedNotificationId, setClickedNotificationId] = useState(null);

  const [loadingLogout, setLoadingLogout] = useState(false);

  const [tempImage, setTempImage] = useState(null);

  const [overlay, setOverlay] = useState(false);

  const [isMobile, setIsMobile] = useState(
    window.innerWidth < 768
  );

  const token = localStorage.getItem("access");


  // ====================================================
  // THEME
  // ====================================================

  const theme = useMemo(() => ({

    background: darkMode
      ? "#091727"
      : COLORS.page,

    sidebar: COLORS.navy,

    sidebarDeep: COLORS.deepNavy,

    header: darkMode
      ? "#102238"
      : COLORS.white,

    card: darkMode
      ? "#14283E"
      : COLORS.white,

    text: darkMode
      ? "#F7F9FC"
      : COLORS.text,

    secondary: darkMode
      ? "#A9BACB"
      : COLORS.muted,

    border: darkMode
      ? "#29415A"
      : COLORS.border,

    primary: COLORS.blue,

    gold: COLORS.gold,

    hover: darkMode
      ? "#1B344E"
      : "#F1F5F9",

    shadow: darkMode
      ? "0 12px 30px rgba(0,0,0,.22)"
      : "0 10px 30px rgba(3,27,53,.055)"

  }), [darkMode]);


  // ====================================================
  // RESPONSIVE SCREEN
  // ====================================================

  useEffect(() => {

    const resize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", resize);

    return () => {
      window.removeEventListener("resize", resize);
    };

  }, []);


  // ====================================================
  // PERSONALIZED WELCOME NAME
  // ====================================================

  const displayName = useMemo(() => {

    if (!user) return "Musu Flomo";

    return (
      user.full_name ||
      user.name ||
      [user.first_name, user.last_name]
        .filter(Boolean)
        .join(" ") ||
      user.username ||
      "Musu Flomo"
    );

  }, [user]);


  // ====================================================
  // PROFILE
  // ====================================================

  const fetchUserProfile = async () => {

    try {

      const response = await api.get("user-profile/");

      setUser(response.data);

    } catch (error) {

      console.error("Profile error:", error);

    } finally {

      setLoading(false);

    }

  };


  const refreshUser = async () => {

    try {

      const response = await api.get("/user-profile/", {

        headers: {
          Authorization: `Bearer ${token}`
        }

      });

      setUser(response.data);

      setTempImage(null);

    } catch (error) {

      console.error("Refresh profile error:", error);

    }

  };


  // ====================================================
  // LINKED STUDENTS
  // ====================================================

  const fetchChildren = async () => {

    try {

      const response = await api.get(
        "/admissions/my-linked-students/",
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      const data = response.data;

      setChildren(
        Array.isArray(data)
          ? data
          : data?.results || []
      );

    } catch (error) {

      console.error("Children error:", error);

    }

  };


  // ====================================================
  // NOTIFICATIONS
  // ====================================================

  const fetchNotifications = async () => {

    try {

      const response = await api.get(
        "api/users/notifications/",
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      const data = Array.isArray(response.data)
        ? response.data
        : response.data?.results || [];

      const sorted = [...data].sort(
        (a, b) =>
          new Date(b.created_at) -
          new Date(a.created_at)
      );

      setNotifications(sorted);

      setUnreadCount(
        data.filter(item => !item.is_read).length
      );

    } catch (error) {

      console.error("Notification error:", error);

    }

  };


  // ====================================================
  // INITIAL DATA LOADING
  // ====================================================

  useEffect(() => {

    fetchUserProfile();

    fetchChildren();

    fetchNotifications();

    refreshUser();

  }, []);


  // ====================================================
  // PROFILE IMAGE UPDATE
  // ====================================================

  const handleImageChange = async (e) => {

    const file = e.target.files?.[0];

    if (!file) return;

    const formData = new FormData();

    formData.append("profile_image", file);

    const preview = URL.createObjectURL(file);

    setTempImage(preview);

    setOverlay(true);

    try {

      await api.put(
        "/user-profile/",
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        }
      );

      Swal.fire({
        icon: "success",
        title: "Profile Updated",
        text: "Your profile image has been updated successfully.",
        confirmButtonColor: COLORS.blue
      });

      await refreshUser();

    } catch (error) {

      console.error(error);

      setTempImage(null);

      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: "Could not update your profile image.",
        confirmButtonColor: COLORS.blue
      });

    } finally {

      setOverlay(false);

      URL.revokeObjectURL(preview);

    }

  };


  // ====================================================
  // MARK NOTIFICATION AS READ
  // ====================================================

  const markAsRead = async (id) => {

    try {

      await api.post(
        `api/users/notifications/${id}/mark_as_read/`,
        {
          is_read: true
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setNotifications(prev =>
        prev.map(item =>
          item.id === id
            ? { ...item, is_read: true }
            : item
        )
      );

      setUnreadCount(prev => Math.max(0, prev - 1));

    } catch (error) {

      console.error("Mark notification error:", error);

    }

  };


  const handleNotificationClick = async (id) => {

    setClickedNotificationId(id);

    await markAsRead(id);

    setTimeout(() => {
      setClickedNotificationId(null);
    }, 500);

  };


  // ====================================================
  // LOGOUT
  // ====================================================

  const handleLogout = async () => {

    setLoadingLogout(true);

    try {

      await api.post("api/logout/", {});

    } catch (error) {

      console.error("Logout error:", error);

    } finally {

      localStorage.removeItem("access");

      navigate("/login");

      setLoadingLogout(false);

    }

  };


  // ====================================================
  // SIDEBAR MENU
  // STUDENT FUNCTIONALITY PRESERVED
  // ====================================================

  const menuItems = [

    {
      key: "dashboard",
      icon: <DashboardOutlined />,
      label: "Dashboard"
    },

    {
      key: "assignments",
      icon: <FileTextOutlined />,
      label: "Assignments"
    }

  ];


  // IMPORTANT:
  // Existing dynamically generated student menu is preserved.

  const childMenuItems = children.map(child => ({

    key: `child_${child.id}`,

    icon: <UserOutlined />,

    label: child.full_name

  }));


  const fullMenu = [

    ...menuItems,

    {
      type: "divider"
    },

    {
      type: "group",
      label: "My Bio",
      children: childMenuItems
    }

  ];


  // ====================================================
  // MENU NAVIGATION
  // ====================================================

  const handleMenuClick = ({ key }) => {

    if (key.startsWith("child_")) {

      const id = key.split("_")[1];

      const child = children.find(
        item => item.id.toString() === id
      );

      setSelectedChild(child);

      setShowGrades(false);

      setShowPayments(false);

    }

    setActivePage(key);

    if (isMobile) {
      setDrawerOpen(false);
    }

  };


  // ====================================================
  // SIDEBAR DESIGN
  // ====================================================

  const sidebar = (

    <div className="zpa-sidebar-inner">

      {/* SCHOOL BRAND */}

      <div className="zpa-brand">

        <div className="zpa-logo-wrapper">

          <img
            src={logo}
            alt="Zion Praise Academy"
            className="zpa-logo"
          />

        </div>

        {!collapsed && (

          <div className="zpa-brand-text">

            <div className="zpa-school-name">
              Zion Praise Academy
            </div>

            <div className="zpa-school-subtitle">
              School Management Portal
            </div>

          </div>

        )}

      </div>


      {/* USER PROFILE */}

      {!collapsed && (

        <div className="zpa-profile-container">

          <div className="zpa-profile-card">

            <ParentAndStudentProfile
              user={user}
              refreshUser={refreshUser}
              tempImage={tempImage}
              setTempImage={setTempImage}
              theme={theme}
              darkMode={darkMode}
            />

          </div>

        </div>

      )}


      {/* SIDEBAR NAVIGATION */}

      <div className="zpa-menu-container">

        <div className="zpa-menu-label">
          {!collapsed && "MAIN MENU"}
        </div>

        <Menu
          className="zpa-sidebar-menu"
          mode="inline"
          items={fullMenu}
          selectedKeys={[activePage]}
          inlineCollapsed={collapsed}
          onClick={handleMenuClick}
          theme="dark"
          style={{
            border: "none",
            background: "transparent",
            fontWeight: 650
          }}
        />

      </div>


      {/* SIDEBAR FOOTER */}

      <div className="zpa-sidebar-footer">

        <div className="zpa-footer-line" />

        {!collapsed && (

          <div>

            <div className="zpa-footer-title">
              ZION PRAISE ACADEMY
            </div>

            <div className="zpa-footer-subtitle">
              Excellence Through Education
            </div>

            <div className="zpa-copyright">
              © 2026 All Rights Reserved
            </div>

          </div>

        )}

      </div>

    </div>

  );


  // ====================================================
  // MOBILE SIDEBAR
  // ====================================================

  const mobileSidebar = (

    <div
      style={{
        height: "100%",
        background: COLORS.navy
      }}
    >
      {sidebar}
    </div>

  );


  const sidebarWidth = collapsed ? 78 : 270;


  const drawerContent = (

    <Drawer
      open={drawerOpen}
      width={280}
      placement="left"
      onClose={() => setDrawerOpen(false)}
      styles={{
        body: {
          padding: 0,
          background: COLORS.navy
        },
        header: {
          display: "none"
        }
      }}
    >

      {mobileSidebar}

    </Drawer>

  );


  // ====================================================
  // MAIN RETURN
  // ====================================================

  return (

    <div
      className="zpa-dashboard"
      style={{
        position: "fixed",
        inset: 0,
        background: theme.background,
        overflow: "hidden"
      }}
    >

      {/* ==================================================
          GLOBAL DASHBOARD ANIMATIONS AND STYLES
      ================================================== */}

      <style>{`

        @keyframes zpaGradientMove {

          0% {
            background-position: 0% 50%;
          }

          50% {
            background-position: 100% 50%;
          }

          100% {
            background-position: 0% 50%;
          }

        }

        @keyframes zpaFloat {

          0%,100% {
            transform: translateY(0px);
          }

          50% {
            transform: translateY(-12px);
          }

        }

        @keyframes zpaGlow {

          0%,100% {
            opacity: .25;
            transform: scale(1);
          }

          50% {
            opacity: .55;
            transform: scale(1.12);
          }

        }

        @keyframes zpaTexture {

          0% {
            transform: translateX(-5%) translateY(0);
          }

          50% {
            transform: translateX(5%) translateY(-2%);
          }

          100% {
            transform: translateX(-5%) translateY(0);
          }

        }

        @keyframes zpaShimmer {

          0% {
            background-position: -200% center;
          }

          100% {
            background-position: 200% center;
          }

        }

        @keyframes zpaFadeIn {

          from {
            opacity: 0;
            transform: translateY(12px);
          }

          to {
            opacity: 1;
            transform: translateY(0);
          }

        }

        .zpa-sidebar-inner {

          height: 100%;
          display: flex;
          flex-direction: column;
          background: linear-gradient(
            180deg,
            #031B35 0%,
            #021426 100%
          );

          overflow: hidden;

        }

        .zpa-brand {

          min-height: 85px;
          display: flex;
          align-items: center;
          padding: 0 17px;
          gap: 12px;
          border-bottom: 1px solid rgba(255,255,255,.10);
          flex-shrink: 0;

        }

        .zpa-logo-wrapper {

          width: 48px;
          height: 48px;
          min-width: 48px;
          border-radius: 13px;
          padding: 2px;
          background: linear-gradient(
            135deg,
            #F6C944,
            #FFE58A,
            #CBA454
          );

          box-shadow: 0 5px 18px rgba(246,201,68,.18);

        }

        .zpa-logo {

          width: 100%;
          height: 100%;
          object-fit: cover;
          border-radius: 11px;
          background: white;

        }

        .zpa-brand-text {

          min-width: 0;
          overflow: hidden;

        }

        .zpa-school-name {

          color: #FFFFFF;
          font-size: 15px;
          font-weight: 900;
          line-height: 1.35;
          white-space: nowrap;

        }

        .zpa-school-subtitle {

          color: #B6C8D9;
          font-size: 11px;
          margin-top: 3px;

        }

        .zpa-profile-container {

          padding: 17px 13px 12px;
          flex-shrink: 0;

        }

        .zpa-profile-card {

          padding: 13px;
          border-radius: 15px;
          background: rgba(255,255,255,.065);
          border: 1px solid rgba(255,255,255,.10);
          box-shadow: 0 8px 25px rgba(0,0,0,.12);

        }

        .zpa-profile-card * {

          color: #FFFFFF;

        }

        .zpa-menu-container {

          flex: 1;
          overflow-y: auto;
          overflow-x: hidden;
          padding: 12px 11px;

        }

        .zpa-menu-container::-webkit-scrollbar {

          width: 5px;

        }

        .zpa-menu-container::-webkit-scrollbar-thumb {

          background: rgba(246,201,68,.45);
          border-radius: 20px;

        }

        .zpa-menu-label {

          color: #829CB5;
          font-size: 10px;
          font-weight: 900;
          letter-spacing: 1.5px;
          padding: 8px 12px 12px;

        }

        .zpa-sidebar-menu.ant-menu-dark {

          background: transparent !important;

        }

        .zpa-sidebar-menu.ant-menu-dark .ant-menu-item {

          color: #D5E0EC !important;
          height: 46px;
          line-height: 46px;
          margin: 5px 0;
          width: 100%;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 650;
          transition: all .25s ease;

        }

        .zpa-sidebar-menu.ant-menu-dark .ant-menu-item .anticon {

          color: #A9BDD1;
          font-size: 17px;
          transition: all .25s ease;

        }

        .zpa-sidebar-menu.ant-menu-dark .ant-menu-item:hover {

          background: rgba(255,255,255,.105) !important;
          color: #FFE58A !important;
          transform: translateX(4px);

        }

        .zpa-sidebar-menu.ant-menu-dark .ant-menu-item:hover .anticon {

          color: #F6C944 !important;
          transform: scale(1.12);

        }

        .zpa-sidebar-menu.ant-menu-dark .ant-menu-item-selected {

          background: linear-gradient(
            100deg,
            rgba(246,201,68,.23),
            rgba(246,201,68,.08)
          ) !important;

          color: #FFE58A !important;
          border-left: 3px solid #F6C944;
          font-weight: 850;

        }

        .zpa-sidebar-menu.ant-menu-dark .ant-menu-item-selected .anticon {

          color: #F6C944 !important;

        }

        .zpa-sidebar-menu.ant-menu-dark .ant-menu-item::after {

          display: none !important;

        }

        .zpa-sidebar-menu.ant-menu-dark .ant-menu-item-group-title {

          color: #829CB5 !important;
          font-size: 10px;
          font-weight: 900;
          letter-spacing: 1.3px;
          padding: 18px 12px 8px;

        }

        .zpa-sidebar-menu.ant-menu-dark .ant-menu-divider {

          border-color: rgba(255,255,255,.12) !important;
          margin: 12px 4px;

        }

        .zpa-sidebar-footer {

          padding: 13px 16px 17px;
          flex-shrink: 0;
          text-align: center;

        }

        .zpa-footer-line {

          height: 1px;
          background: rgba(255,255,255,.12);
          margin-bottom: 14px;

        }

        .zpa-footer-title {

          font-size: 10px;
          font-weight: 900;
          color: #F6C944;
          letter-spacing: 1.1px;

        }

        .zpa-footer-subtitle {

          font-size: 10px;
          color: #A9BDD1;
          margin-top: 4px;

        }

        .zpa-copyright {

          color: #829CB5;
          font-size: 9px;
          margin-top: 9px;

        }

        .zpa-welcome {

          position: relative;
          overflow: hidden;
          min-height: 235px;
          padding: 32px 34px;
          border-radius: 20px;

          background: linear-gradient(
            120deg,
            #021426,
            #031B35,
            #063A6B,
            #062A4B,
            #031B35
          );

          background-size: 300% 300%;
          animation: zpaGradientMove 14s ease infinite;

          box-shadow: 0 15px 40px rgba(3,27,53,.18);

          isolation: isolate;

        }

        .zpa-welcome::before {

          content: "";
          position: absolute;
          inset: 0;
          z-index: -1;

          background:
            radial-gradient(
              circle at 82% 20%,
              rgba(246,201,68,.24),
              transparent 29%
            ),
            radial-gradient(
              circle at 15% 90%,
              rgba(15,109,171,.35),
              transparent 35%
            );

          animation: zpaTexture 10s ease-in-out infinite;

        }

        .zpa-welcome::after {

          content: "";
          position: absolute;
          inset: 0;
          z-index: -1;
          opacity: .16;

          background-image:
            radial-gradient(
              rgba(255,255,255,.8) .7px,
              transparent .7px
            );

          background-size: 22px 22px;

        }

        .zpa-welcome-glow {

          position: absolute;
          width: 260px;
          height: 260px;
          right: 8%;
          top: -100px;
          border-radius: 50%;

          background: rgba(246,201,68,.13);

          filter: blur(35px);

          animation: zpaGlow 7s ease-in-out infinite;

          pointer-events: none;

        }

        .zpa-welcome-decoration {

          position: absolute;
          right: 7%;
          bottom: -95px;
          width: 260px;
          height: 260px;
          border-radius: 50%;

          border: 1px solid rgba(246,201,68,.24);

          box-shadow:
            0 0 0 25px rgba(246,201,68,.035),
            0 0 0 55px rgba(255,255,255,.025);

          pointer-events: none;

        }

        .zpa-welcome-content {

          position: relative;
          z-index: 2;
          max-width: 680px;
          animation: zpaFadeIn .7s ease both;

        }

        .zpa-welcome-label {

          display: inline-flex;
          align-items: center;
          gap: 8px;
          color: #FFE58A;
          font-size: 11px;
          font-weight: 900;
          letter-spacing: 1.8px;
          margin-bottom: 16px;

        }

        .zpa-welcome-label::before {

          content: "";
          width: 23px;
          height: 2px;
          background: #F6C944;
          border-radius: 10px;

        }

        .zpa-welcome-title {

          font-size: clamp(25px, 3vw, 36px);
          font-weight: 900;
          color: #FFFFFF;
          line-height: 1.25;
          letter-spacing: -.8px;

        }

        .zpa-welcome-name {

          color: #FFE58A;

          background: linear-gradient(
            90deg,
            #F6C944,
            #FFE58A,
            #CBA454,
            #FFE58A
          );

          background-size: 200% auto;
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;

          animation: zpaShimmer 6s linear infinite;

        }

        .zpa-welcome-description {

          color: #D3DFEC;
          font-size: 14px;
          line-height: 1.8;
          margin-top: 13px;
          max-width: 550px;

        }

        .zpa-welcome-divider {

          height: 3px;
          width: 52px;
          border-radius: 10px;
          background: linear-gradient(
            90deg,
            #F6C944,
            #FFE58A
          );
          margin-top: 22px;

        }

        .zpa-content-section {

          animation: zpaFadeIn .5s ease both;

        }

        .zpa-section-heading {

          font-weight: 900 !important;
          color: inherit;

        }

        @media(max-width: 767px) {

          .zpa-welcome {

            min-height: 215px;
            padding: 26px 21px;
            border-radius: 16px;

          }

          .zpa-welcome-description {

            font-size: 12px;

          }

          .zpa-welcome-decoration {

            right: -100px;
            opacity: .6;

          }

        }

      `}</style>


      <Layout
        style={{
          height: "100vh",
          background: theme.background
        }}
      >

        {/* ==================================================
            DESKTOP SIDEBAR
        ================================================== */}

        {!isMobile && (

          <Sider
            width={sidebarWidth}
            collapsed={collapsed}
            collapsedWidth={78}
            style={{
              height: "100vh",
              position: "fixed",
              left: 0,
              top: 0,
              bottom: 0,
              background: COLORS.navy,
              zIndex: 20,
              overflow: "hidden",
              boxShadow: "5px 0 25px rgba(2,20,38,.08)"
            }}
          >

            {sidebar}

          </Sider>

        )}


        {/* MOBILE DRAWER */}

        {isMobile && drawerContent}


        <Layout
          style={{
            marginLeft: isMobile ? 0 : sidebarWidth,
            height: "100vh",
            background: theme.background
          }}
        >


          {/* ==================================================
              HEADER
          ================================================== */}



<Header
  style={{
    height: isMobile ? 65 : 75,
    minHeight: isMobile ? 65 : 75,
    padding: isMobile ? "0 10px" : "0 25px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: theme.header,
    borderBottom: `1px solid ${theme.border}`,
    position: "sticky",
    top: 0,
    zIndex: 10,
    boxShadow: "0 3px 15px rgba(3,27,53,.035)",
    overflow: "hidden"
  }}
>

  {/* LEFT SIDE: MENU + BRANDING */}
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: isMobile ? 7 : 15,
      minWidth: 0,
      flexShrink: 1,
      height: "100%"
    }}
  >

    <Button
      shape="circle"
      size="large"
      icon={
        isMobile || collapsed
          ? <MenuUnfoldOutlined />
          : <MenuFoldOutlined />
      }
      onClick={() => {
        if (isMobile) {
          setDrawerOpen(true);
        } else {
          setCollapsed(!collapsed);
        }
      }}
    />

    {isMobile ? (

      /* MOBILE GRAPHIC-STYLE ZPA STUDENT PORTAL */
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          whiteSpace: "nowrap",
          flexWrap: "nowrap",
          height: "59px",
          lineHeight: 1,
          gap: 0,
          flexShrink: 1,
          overflow: "hidden",
          fontFamily: "'Arial Black', 'Impact', 'Roboto Condensed', sans-serif",
          fontSize: "clamp(16px, 5.4vw, 24px)",
          fontWeight: 1000,
          letterSpacing: "-1.3px",
          textTransform: "uppercase",
          transform: "scaleY(1.08)",
          transformOrigin: "center"
        }}
      >

        <span
          style={{
            color: "#000000",
            WebkitTextStroke: "0.6px #000000",
            textShadow: "1px 1px 0 rgba(0,0,0,.12)"
          }}
        >
          ZPA
        </span>

        <span
          style={{
            color: "#B8860B",
            WebkitTextStroke: "0.6px #B8860B",
            textShadow: "1px 1px 0 rgba(184,134,11,.2)"
          }}
        >
          STUDENT
        </span>

        <span
          style={{
            color: "#000000",
            WebkitTextStroke: "0.6px #000000",
            textShadow: "1px 1px 0 rgba(0,0,0,.12)"
          }}
        >
          PORTAL
        </span>

      </div>

    ) : (

      /* DESKTOP TITLE */
      <div>
        <Typography
          style={{
            fontSize: 20,
            fontWeight: 900,
            color: theme.text,
            lineHeight: 1.3
          }}
        >
          Student Dashboard
        </Typography>

        <Typography
          style={{
            fontSize: 11,
            color: theme.secondary
          }}
        >
          Zion Praise Academy · School Management System
        </Typography>
      </div>

    )}

  </div>


  {/* RIGHT SIDE: ACTIONS */}
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: isMobile ? 6 : 15,
      flexShrink: 0
    }}
  >

    {/* DARK MODE */}
    <Button
      shape="circle"
      onClick={() => setDarkMode(!darkMode)}
      style={{
        background: darkMode ? "#263F59" : "#F1F5F9",
        border: "none"
      }}
    >
      {darkMode ? "☀️" : "🌙"}
    </Button>


    {/* NOTIFICATIONS */}
    <Dropdown
      open={notificationOpen}
      onOpenChange={setNotificationOpen}
      placement="bottomRight"
      trigger={["click"]}
      dropdownRender={() => (
        <div
          style={{
            width: 330,
            maxWidth: "90vw",
            background: theme.card,
            borderRadius: 16,
            padding: 15,
            boxShadow: "0 15px 40px rgba(0,0,0,.15)",
            border: `1px solid ${theme.border}`,
            maxHeight: 380,
            overflowY: "auto"
          }}
        >

          <Typography
            style={{
              fontWeight: 900,
              fontSize: 16,
              color: theme.text,
              marginBottom: 12,
              textAlign: "center"
            }}
          >
            Notifications
          </Typography>

          {notifications.length > 0 ? (

            notifications.map(item => (

              <div
                key={item.id}
                onClick={() => handleNotificationClick(item.id)}
                style={{
                  padding: "12px 10px",
                  borderRadius: 11,
                  cursor: "pointer",
                  marginBottom: 8,
                  background: item.is_read
                    ? "transparent"
                    : darkMode
                      ? "#263F59"
                      : "#F1F5F9",
                  border: `1px solid ${theme.border}`,
                  transition: "all .2s ease",
                  opacity: clickedNotificationId === item.id ? .65 : 1
                }}
              >

                <Typography
                  style={{
                    fontWeight: item.is_read ? 500 : 800,
                    fontSize: 13,
                    color: theme.text
                  }}
                >
                  {item.message}
                </Typography>

                <Text
                  style={{
                    fontSize: 11,
                    color: theme.secondary
                  }}
                >
                  {new Date(item.created_at).toLocaleString()}
                </Text>

              </div>

            ))

          ) : (

            <Typography
              style={{
                textAlign: "center",
                color: theme.secondary,
                padding: 15
              }}
            >
              No notifications
            </Typography>

          )}

        </div>
      )}
    >

      <Badge
        count={unreadCount}
        offset={[0, 0]}
      >
        <Button
          shape="circle"
          icon={<NotificationOutlined />}
        />
      </Badge>

    </Dropdown>


    {/* LOGOUT */}
    <Button
      danger
      shape={isMobile ? "circle" : "default"}
      icon={<LogoutOutlined />}
      loading={loadingLogout}
      onClick={handleLogout}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        fontWeight: 700
      }}
    >
      {!isMobile && "Logout"}
    </Button>

  </div>

</Header>



          {/* ==================================================
              MAIN CONTENT
          ================================================== */}

          <Content
            style={{
              padding: isMobile ? 13 : 25,
              background: theme.background,
              height: "calc(100vh - 75px)",
              overflowY: "auto"
            }}
          >

            <motion.div
              initial={{
                opacity: 0,
                y: 15
              }}
              animate={{
                opacity: 1,
                y: 0
              }}
              transition={{
                duration: .4
              }}
            >


              {/* ==================================================
                  DASHBOARD HOME
                  STATISTIC CARDS REMOVED
              ================================================== */}

              {activePage === "dashboard" && (

                <Box
                  className="zpa-content-section"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 3
                  }}
                >




                  {/* ==========================================
                      EXISTING BIO LINK PROFILE
                  ========================================== */}

                  <Box
                    sx={{
                      background: theme.card,
                      borderRadius: 3,
                      overflow: "hidden",
                      border: `1px solid ${theme.border}`,
                      boxShadow: theme.shadow
                    }}
                  >

                    <BioLinkProfile
                      user={user}
                      dashboardBanner={dashboardBanner}
                    />

                  </Box>


                  {/* ==========================================
                      PARENT ANALYTICS
                  ========================================== */}

                  <Box
                    sx={{
                      background: theme.card,
                      borderRadius: 4,
                      p: {
                        xs: 2,
                        md: 3
                      },
                      boxShadow: theme.shadow,
                      border: `1px solid ${theme.border}`
                    }}
                  >

                    {/* SECTION TITLE */}

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        mb: 3
                      }}
                    >

                      <Box
                        sx={{
                          width: 43,
                          height: 43,
                          borderRadius: 2.5,
                          background: darkMode
                            ? "rgba(246,201,68,.13)"
                            : "#FFF8E7",
                          color: COLORS.antique,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 22
                        }}
                      >

                        <FaGraduationCap />

                      </Box>

                      <Box>

                        <Typography
                          sx={{
                            fontWeight: 900,
                            fontSize: 21,
                            color: theme.text
                          }}
                        >
                          Parent Analytics
                        </Typography>

                        <Typography
                          sx={{
                            fontSize: 12,
                            color: theme.secondary
                          }}
                        >
                          Quick access to important school information
                        </Typography>

                      </Box>

                    </Box>


                    <Grid
                      container
                      spacing={2.5}
                    >

                      {/* ACADEMIC PROGRESS */}

                      <Grid
                        item
                        xs={12}
                        md={4}
                      >

                        <motion.div
                          whileHover={{ y: -6 }}
                          transition={{ duration: .2 }}
                        >

                          <Box
                            sx={{
                              height: 190,
                              borderRadius: 3,
                              background: darkMode
                                ? "linear-gradient(145deg,#14283E,#1C3C5B)"
                                : "linear-gradient(145deg,#F7F9FC,#EAF1F8)",
                              border: `1px solid ${theme.border}`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexDirection: "column",
                              gap: 1.5,
                              transition: "all .25s ease"
                            }}
                          >

                            <Box
                              sx={{
                                width: 65,
                                height: 65,
                                borderRadius: 3,
                                background: "linear-gradient(135deg,#021426,#063A6B)",
                                color: "#FFFFFF",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 30,
                                boxShadow: "0 8px 20px rgba(6,42,75,.20)"
                              }}
                            >

                              <FaGraduationCap />

                            </Box>

                            <Typography
                              sx={{
                                fontWeight: 850,
                                color: theme.text,
                                fontSize: 15
                              }}
                            >
                              Academic Progress
                            </Typography>

                            <Typography
                              sx={{
                                color: theme.secondary,
                                fontSize: 12
                              }}
                            >
                              Monitor student performance
                            </Typography>

                          </Box>

                        </motion.div>

                      </Grid>


                      {/* ASSIGNMENTS */}

                      <Grid
                        item
                        xs={12}
                        md={4}
                      >

                        <motion.div
                          whileHover={{ y: -6 }}
                          transition={{ duration: .2 }}
                        >

                          <Box
                            sx={{
                              height: 190,
                              borderRadius: 3,
                              background: darkMode
                                ? "linear-gradient(145deg,#14283E,#1C3C5B)"
                                : "linear-gradient(145deg,#F7F9FC,#EAF1F8)",
                              border: `1px solid ${theme.border}`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexDirection: "column",
                              gap: 1.5
                            }}
                          >

                            <Box
                              sx={{
                                width: 65,
                                height: 65,
                                borderRadius: 3,
                                background: "linear-gradient(135deg,#063A6B,#0F6DAB)",
                                color: "#FFFFFF",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 29,
                                boxShadow: "0 8px 20px rgba(6,58,107,.20)"
                              }}
                            >

                              <FaBookReader />

                            </Box>

                            <Typography
                              sx={{
                                fontWeight: 850,
                                color: theme.text,
                                fontSize: 15
                              }}
                            >
                              Assignments
                            </Typography>

                            <Typography
                              sx={{
                                color: theme.secondary,
                                fontSize: 12
                              }}
                            >
                              Access student assignments
                            </Typography>

                          </Box>

                        </motion.div>

                      </Grid>


                      {/* SCHOOL EVENTS */}

                      <Grid
                        item
                        xs={12}
                        md={4}
                      >

                        <motion.div
                          whileHover={{ y: -6 }}
                          transition={{ duration: .2 }}
                        >

                          <Box
                            sx={{
                              height: 190,
                              borderRadius: 3,
                              background: darkMode
                                ? "linear-gradient(145deg,#14283E,#1C3C5B)"
                                : "linear-gradient(145deg,#F7F9FC,#EAF1F8)",
                              border: `1px solid ${theme.border}`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexDirection: "column",
                              gap: 1.5
                            }}
                          >

                            <Box
                              sx={{
                                width: 65,
                                height: 65,
                                borderRadius: 3,
                                background: "linear-gradient(135deg,#C0750D,#F6C944)",
                                color: "#FFFFFF",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 29,
                                boxShadow: "0 8px 20px rgba(203,164,84,.20)"
                              }}
                            >

                              <CalendarOutlined />

                            </Box>

                            <Typography
                              sx={{
                                fontWeight: 850,
                                color: theme.text,
                                fontSize: 15
                              }}
                            >
                              School Events
                            </Typography>

                            <Typography
                              sx={{
                                color: theme.secondary,
                                fontSize: 12
                              }}
                            >
                              Stay informed about school activities
                            </Typography>

                          </Box>

                        </motion.div>

                      </Grid>

                    </Grid>

                  </Box>

                </Box>

              )}


              {/* ==================================================
                  ASSIGNMENTS PAGE
              ================================================== */}

              {activePage === "assignments" && (

                <Box
                  sx={{
                    background: theme.card,
                    borderRadius: 4,
                    p: {
                      xs: 2,
                      md: 4
                    },
                    border: `1px solid ${theme.border}`,
                    boxShadow: theme.shadow,
                    minHeight: "80vh"
                  }}
                >

                  <AssignmentView />

                </Box>

              )}




{/* ==================================================
    PROFESSIONAL STUDENT PROFILE
================================================== */}

{activePage.startsWith("child_") && selectedChild && (

    <Box
        sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
            pb: 3,
        }}
    >

        {/* ==================================================
            STUDENT PROFILE HEADER
        ================================================== */}

        <Box
            sx={{
                position: "relative",
                overflow: "hidden",
                borderRadius: 3,
                background: darkMode
                    ? "linear-gradient(120deg, #102238, #173C5B)"
                    : "linear-gradient(120deg, #FFFFFF, #F4F8FC)",
                border: `1px solid ${theme.border}`,
                boxShadow: theme.shadow,
            }}
        >

            {/* Top accent */}
            <Box
                sx={{
                    height: 4,
                    background:
                        "linear-gradient(90deg, #031B35, #063A6B, #F6C944, #CBA454)",
                }}
            />

            <Box
                sx={{
                    position: "absolute",
                    width: 220,
                    height: 220,
                    borderRadius: "50%",
                    right: -100,
                    top: -130,
                    background: "rgba(246,201,68,.08)",
                    pointerEvents: "none",
                }}
            />

            <Box
                sx={{
                    position: "relative",
                    p: {
                        xs: 2,
                        sm: 2.5,
                        md: 3,
                    },
                    display: "flex",
                    alignItems: "center",
                    flexDirection: {
                        xs: "column",
                        sm: "row",
                    },
                    gap: 2.5,
                }}
            >

                {/* Student image */}
                <Box
                    sx={{
                        position: "relative",
                        width: {
                            xs: 100,
                            sm: 108,
                        },
                        height: {
                            xs: 100,
                            sm: 108,
                        },
                        flexShrink: 0,
                        p: "3px",
                        borderRadius: "50%",
                        background:
                            "linear-gradient(135deg, #C0750D, #F6C944, #FFE58A)",
                        boxShadow:
                            "0 8px 22px rgba(203,164,84,.20)",
                    }}
                >

                    <Avatar
                        src={
                            selectedChild.student_admission_image ||
                            defaultAvatar
                        }
                        alt={selectedChild.full_name}
                        sx={{
                            width: "100%",
                            height: "100%",
                            border: `3px solid ${darkMode ? "#102238" : "#FFFFFF"}`,
                            boxSizing: "border-box",
                        }}
                    />

                    {/* Student status indicator */}
                    <Box
                        sx={{
                            position: "absolute",
                            width: 17,
                            height: 17,
                            bottom: 5,
                            right: 4,
                            borderRadius: "50%",
                            background: COLORS.green,
                            border: `3px solid ${darkMode ? "#102238" : "#FFFFFF"}`,
                        }}
                    />

                </Box>


                {/* Student information */}
                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        textAlign: {
                            xs: "center",
                            sm: "left",
                        },
                    }}
                >

                    <Typography
                        sx={{
                            color: theme.secondary,
                            fontSize: 10,
                            fontWeight: 900,
                            letterSpacing: 1.5,
                            textTransform: "uppercase",
                            mb: 0.5,
                        }}
                    >
                        Student Profile
                    </Typography>

                    <Typography
                        sx={{
                            color: theme.text,
                            fontSize: {
                                xs: 22,
                                sm: 25,
                                md: 28,
                            },
                            fontWeight: 900,
                            lineHeight: 1.25,
                            overflowWrap: "anywhere",
                        }}
                    >
                        {selectedChild.full_name || "Student"}
                    </Typography>

                    <Typography
                        sx={{
                            color: theme.secondary,
                            fontSize: 12,
                            mt: 0.5,
                        }}
                    >
                        Zion Praise Academy · Student Information
                    </Typography>


                    {/* Grade and student ID */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            justifyContent: {
                                xs: "center",
                                sm: "flex-start",
                            },
                            gap: 1,
                            mt: 1.7,
                        }}
                    >

                        <Box
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.7,
                                px: 1.4,
                                py: 0.7,
                                borderRadius: 1.5,
                                background: darkMode
                                    ? "rgba(246,201,68,.13)"
                                    : "#FFF7DF",
                                border: "1px solid rgba(246,201,68,.35)",
                            }}
                        >

                            <FaGraduationCap
                                style={{
                                    color: COLORS.orange,
                                    fontSize: 13,
                                }}
                            />

                            <Typography
                                sx={{
                                    fontSize: 11,
                                    fontWeight: 800,
                                    color: darkMode
                                        ? COLORS.goldLight
                                        : COLORS.navy,
                                }}
                            >
                                {selectedChild.grade_class_name || "Grade not assigned"}
                            </Typography>

                        </Box>


                        <Box
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.7,
                                px: 1.4,
                                py: 0.7,
                                borderRadius: 1.5,
                                background: darkMode
                                    ? "rgba(15,109,171,.18)"
                                    : "#EAF3FA",
                                border: "1px solid rgba(15,109,171,.18)",
                            }}
                        >

                            <UserOutlined
                                style={{
                                    color: COLORS.sky,
                                    fontSize: 12,
                                }}
                            />

                            <Typography
                                sx={{
                                    fontSize: 11,
                                    fontWeight: 800,
                                    color: darkMode
                                        ? "#B9DDF5"
                                        : COLORS.navy,
                                }}
                            >
                                Student ID: {selectedChild.id || "N/A"}
                            </Typography>

                        </Box>

                    </Box>

                </Box>


                {/* Student actions */}
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: {
                            xs: "row",
                            sm: "column",
                        },
                        gap: 1,
                        width: {
                            xs: "100%",
                            sm: "auto",
                        },
                        flexShrink: 0,
                    }}
                >

                    <Button
                        type="primary"
                        icon={<ReadOutlined />}
                        onClick={() => {
                            setShowGrades(true);
                            setShowPayments(false);
                        }}
                        style={{
                            height: 39,
                            borderRadius: 8,
                            fontWeight: 800,
                            background: COLORS.navyBlue,
                            borderColor: COLORS.navyBlue,
                            flex: 1,
                        }}
                    >
                        View Grades
                    </Button>

                    <Button
                        icon={<WalletOutlined />}
                        onClick={() => {
                            setShowGrades(false);
                            setShowPayments(true);
                        }}
                        style={{
                            height: 39,
                            borderRadius: 8,
                            fontWeight: 800,
                            background: COLORS.gold,
                            borderColor: COLORS.gold,
                            color: COLORS.navy,
                            flex: 1,
                        }}
                    >
                        Payments
                    </Button>

                </Box>

            </Box>

        </Box>


        {/* ==================================================
            GRADES VIEW
        ================================================== */}

        {showGrades ? (

            <Box
                sx={{
                    background: theme.card,
                    borderRadius: 3,
                    p: {
                        xs: 2,
                        md: 3,
                    },
                    border: `1px solid ${theme.border}`,
                    boxShadow: theme.shadow,
                }}
            >

                <Button
                    onClick={() => setShowGrades(false)}
                    style={{
                        marginBottom: 20,
                        height: 38,
                        borderRadius: 8,
                        fontWeight: 800,
                        background: COLORS.navyBlue,
                        color: "#FFFFFF",
                        border: "none",
                    }}
                >
                    ← Back to Student Overview
                </Button>

                <ParentsStudentGrades
                    selectedChild={selectedChild}
                    onBack={() => setShowGrades(false)}
                />

            </Box>

        ) : showPayments ? (

            /* ==================================================
                PAYMENT TRANSACTIONS
            ================================================== */

            <Box
                sx={{
                    background: theme.card,
                    borderRadius: 3,
                    p: {
                        xs: 2,
                        md: 3,
                    },
                    border: `1px solid ${theme.border}`,
                    boxShadow: theme.shadow,
                }}
            >

                <Button
                    onClick={() => setShowPayments(false)}
                    style={{
                        marginBottom: 20,
                        height: 38,
                        borderRadius: 8,
                        fontWeight: 800,
                        background: COLORS.navyBlue,
                        color: "#FFFFFF",
                        border: "none",
                    }}
                >
                    ← Back to Student Overview
                </Button>

                <TransActionPerStudent
                    selectedChild={selectedChild}
                    onBack={() => setShowPayments(false)}
                />

            </Box>

        ) : (

            /* ==================================================
                STUDENT OVERVIEW
            ================================================== */

            <>

                {/* ==================================================
                    ATTENDANCE SUMMARY
                ================================================== */}

                <Box
                    sx={{
                        background: theme.card,
                        border: `1px solid ${theme.border}`,
                        borderRadius: 3,
                        p: {
                            xs: 2,
                            md: 2.5,
                        },
                        boxShadow: theme.shadow,
                    }}
                >

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 2,
                            mb: 2.5,
                        }}
                    >

                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.2,
                            }}
                        >

                            <Box
                                sx={{
                                    width: 38,
                                    height: 38,
                                    borderRadius: 2,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    background: darkMode
                                        ? "rgba(15,109,171,.18)"
                                        : "#EAF3FA",
                                    color: COLORS.sky,
                                    fontSize: 18,
                                }}
                            >
                                <CalendarOutlined />
                            </Box>

                            <Box>

                                <Typography
                                    sx={{
                                        color: theme.text,
                                        fontWeight: 900,
                                        fontSize: 16,
                                    }}
                                >
                                    Attendance Summary
                                </Typography>

                                <Typography
                                    sx={{
                                        color: theme.secondary,
                                        fontSize: 11,
                                    }}
                                >
                                    Student attendance overview
                                </Typography>

                            </Box>

                        </Box>

                    </Box>


                    <Grid container spacing={1.5}>

                        {/* PRESENT */}
                        <Grid item xs={4}>

                            <Box
                                sx={{
                                    p: {
                                        xs: 1.3,
                                        sm: 2,
                                    },
                                    borderRadius: 2.5,
                                    background: darkMode
                                        ? "rgba(79,174,34,.11)"
                                        : "#F0F8EB",
                                    border: "1px solid rgba(79,174,34,.17)",
                                    textAlign: "center",
                                    height: "100%",
                                    boxSizing: "border-box",
                                }}
                            >

                                <Box
                                    sx={{
                                        width: 30,
                                        height: 30,
                                        borderRadius: "50%",
                                        background: "rgba(79,174,34,.13)",
                                        color: COLORS.green,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        mx: "auto",
                                        mb: 1,
                                        fontSize: 15,
                                    }}
                                >
                                    <icon />
                                </Box>

                                <Typography
                                    sx={{
                                        fontSize: 11,
                                        fontWeight: 800,
                                        color: COLORS.green,
                                    }}
                                >
                                    Present
                                </Typography>

                                <Typography
                                    sx={{
                                        fontSize: {
                                            xs: 23,
                                            sm: 28,
                                        },
                                        fontWeight: 900,
                                        color: COLORS.green,
                                        lineHeight: 1.4,
                                    }}
                                >
                                    {selectedChild.total_present || 0}
                                </Typography>

                            </Box>

                        </Grid>


                        {/* ABSENT */}
                        <Grid item xs={4}>

                            <Box
                                sx={{
                                    p: {
                                        xs: 1.3,
                                        sm: 2,
                                    },
                                    borderRadius: 2.5,
                                    background: darkMode
                                        ? "rgba(239,68,68,.11)"
                                        : "#FEF2F2",
                                    border: "1px solid rgba(239,68,68,.17)",
                                    textAlign: "center",
                                    height: "100%",
                                    boxSizing: "border-box",
                                }}
                            >

                                <Box
                                    sx={{
                                        width: 30,
                                        height: 30,
                                        borderRadius: "50%",
                                        background: "rgba(239,68,68,.12)",
                                        color: "#EF4444",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        mx: "auto",
                                        mb: 1,
                                        fontSize: 15,
                                    }}
                                >
                                    <CalendarOutlined />
                                </Box>

                                <Typography
                                    sx={{
                                        fontSize: 11,
                                        fontWeight: 800,
                                        color: "#DC2626",
                                    }}
                                >
                                    Absent
                                </Typography>

                                <Typography
                                    sx={{
                                        fontSize: {
                                            xs: 23,
                                            sm: 28,
                                        },
                                        fontWeight: 900,
                                        color: "#EF4444",
                                        lineHeight: 1.4,
                                    }}
                                >
                                    {selectedChild.total_absent || 0}
                                </Typography>

                            </Box>

                        </Grid>


                        {/* LATE */}
                        <Grid item xs={4}>

                            <Box
                                sx={{
                                    p: {
                                        xs: 1.3,
                                        sm: 2,
                                    },
                                    borderRadius: 2.5,
                                    background: darkMode
                                        ? "rgba(246,201,68,.10)"
                                        : "#FFF8E7",
                                    border: "1px solid rgba(246,201,68,.25)",
                                    textAlign: "center",
                                    height: "100%",
                                    boxSizing: "border-box",
                                }}
                            >

                                <Box
                                    sx={{
                                        width: 30,
                                        height: 30,
                                        borderRadius: "50%",
                                        background: "rgba(246,201,68,.16)",
                                        color: COLORS.orange,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        mx: "auto",
                                        mb: 1,
                                        fontSize: 15,
                                    }}
                                >
                                    <CalendarOutlined />
                                </Box>

                                <Typography
                                    sx={{
                                        fontSize: 11,
                                        fontWeight: 800,
                                        color: COLORS.orange,
                                    }}
                                >
                                    Late
                                </Typography>

                                <Typography
                                    sx={{
                                        fontSize: {
                                            xs: 23,
                                            sm: 28,
                                        },
                                        fontWeight: 900,
                                        color: COLORS.orange,
                                        lineHeight: 1.4,
                                    }}
                                >
                                    {selectedChild.total_late || 0}
                                </Typography>

                            </Box>

                        </Grid>

                    </Grid>

                </Box>


                {/* ==================================================
                    FEES INFORMATION
                ================================================== */}

                <Box
                    sx={{
                        background: theme.card,
                        border: `1px solid ${theme.border}`,
                        borderRadius: 3,
                        p: {
                            xs: 2,
                            md: 2.5,
                        },
                        boxShadow: theme.shadow,
                    }}
                >

                    {/*
                        Keep the existing backend field names.
                    */}
                    {(() => {

                        const totalFee = Number(selectedChild.total_fee || 0);
                        const amountPaid = Number(selectedChild.amount_paid || 0);
                        const balance = Number(selectedChild.balance_fee || 0);

                        const paymentPercentage = totalFee > 0
                            ? Math.min(
                                100,
                                Math.max(0, (amountPaid / totalFee) * 100)
                            )
                            : 0;

                        const money = (value) =>
                            `$${Number(value).toLocaleString("en-US", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            })}`;

                        return (

                            <>

                                {/* Heading */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 2,
                                        mb: 2.5,
                                    }}
                                >

                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1.2,
                                        }}
                                    >

                                        <Box
                                            sx={{
                                                width: 38,
                                                height: 38,
                                                borderRadius: 2,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                background: darkMode
                                                    ? "rgba(246,201,68,.13)"
                                                    : "#FFF7DF",
                                                color: COLORS.orange,
                                                fontSize: 18,
                                            }}
                                        >
                                            <WalletOutlined />
                                        </Box>

                                        <Box>

                                            <Typography
                                                sx={{
                                                    color: theme.text,
                                                    fontWeight: 900,
                                                    fontSize: 16,
                                                }}
                                            >
                                                Fees Information
                                            </Typography>

                                            <Typography
                                                sx={{
                                                    color: theme.secondary,
                                                    fontSize: 11,
                                                }}
                                            >
                                                Student financial summary
                                            </Typography>

                                        </Box>

                                    </Box>

                                    <Box
                                        sx={{
                                            px: 1.2,
                                            py: 0.6,
                                            borderRadius: 2,
                                            background: balance <= 0
                                                ? darkMode
                                                    ? "rgba(79,174,34,.13)"
                                                    : "#EFF9EA"
                                                : darkMode
                                                    ? "rgba(246,201,68,.12)"
                                                    : "#FFF8E7",
                                            color: balance <= 0
                                                ? COLORS.green
                                                : COLORS.orange,
                                            fontSize: 10,
                                            fontWeight: 900,
                                        }}
                                    >
                                        {balance <= 0 ? "FULLY PAID" : "PAYMENT PENDING"}
                                    </Box>

                                </Box>


                                {/* Financial figures */}
                                <Grid container spacing={1.5}>

                                    <Grid item xs={12} sm={4}>

                                        <Box
                                            sx={{
                                                p: 1.8,
                                                borderRadius: 2,
                                                background: theme.hover,
                                                border: `1px solid ${theme.border}`,
                                            }}
                                        >

                                            <Typography
                                                sx={{
                                                    fontSize: 11,
                                                    color: theme.secondary,
                                                    fontWeight: 700,
                                                }}
                                            >
                                                Total Fee
                                            </Typography>

                                            <Typography
                                                sx={{
                                                    mt: 0.7,
                                                    color: theme.text,
                                                    fontWeight: 900,
                                                    fontSize: 20,
                                                }}
                                            >
                                                {money(totalFee)}
                                            </Typography>

                                        </Box>

                                    </Grid>


                                    <Grid item xs={12} sm={4}>

                                        <Box
                                            sx={{
                                                p: 1.8,
                                                borderRadius: 2,
                                                background: darkMode
                                                    ? "rgba(79,174,34,.10)"
                                                    : "#F0F8EB",
                                                border: "1px solid rgba(79,174,34,.18)",
                                            }}
                                        >

                                            <Typography
                                                sx={{
                                                    fontSize: 11,
                                                    color: COLORS.green,
                                                    fontWeight: 700,
                                                }}
                                            >
                                                Amount Paid
                                            </Typography>

                                            <Typography
                                                sx={{
                                                    mt: 0.7,
                                                    color: COLORS.green,
                                                    fontWeight: 900,
                                                    fontSize: 20,
                                                }}
                                            >
                                                {money(amountPaid)}
                                            </Typography>

                                        </Box>

                                    </Grid>


                                    <Grid item xs={12} sm={4}>

                                        <Box
                                            sx={{
                                                p: 1.8,
                                                borderRadius: 2,
                                                background: balance <= 0
                                                    ? darkMode
                                                        ? "rgba(79,174,34,.10)"
                                                        : "#F0F8EB"
                                                    : darkMode
                                                        ? "rgba(239,68,68,.10)"
                                                        : "#FEF2F2",
                                                border: balance <= 0
                                                    ? "1px solid rgba(79,174,34,.18)"
                                                    : "1px solid rgba(239,68,68,.18)",
                                            }}
                                        >

                                            <Typography
                                                sx={{
                                                    fontSize: 11,
                                                    color: balance <= 0
                                                        ? COLORS.green
                                                        : "#DC2626",
                                                    fontWeight: 700,
                                                }}
                                            >
                                                Outstanding Balance
                                            </Typography>

                                            <Typography
                                                sx={{
                                                    mt: 0.7,
                                                    color: balance <= 0
                                                        ? COLORS.green
                                                        : "#EF4444",
                                                    fontWeight: 900,
                                                    fontSize: 20,
                                                }}
                                            >
                                                {money(balance)}
                                            </Typography>

                                        </Box>

                                    </Grid>

                                </Grid>


                                {/* Payment progress */}
                                <Box sx={{ mt: 2.5 }}>

                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            mb: 1,
                                        }}
                                    >

                                        <Typography
                                            sx={{
                                                fontSize: 11,
                                                color: theme.secondary,
                                                fontWeight: 700,
                                            }}
                                        >
                                            Payment Progress
                                        </Typography>

                                        <Typography
                                            sx={{
                                                fontSize: 12,
                                                color: theme.text,
                                                fontWeight: 900,
                                            }}
                                        >
                                            {paymentPercentage.toFixed(1)}%
                                        </Typography>

                                    </Box>

                                    <Box
                                        sx={{
                                            height: 9,
                                            borderRadius: 20,
                                            overflow: "hidden",
                                            background: darkMode
                                                ? "#29415A"
                                                : "#E9EEF2",
                                        }}
                                    >

                                        <Box
                                            sx={{
                                                width: `${paymentPercentage}%`,
                                                height: "100%",
                                                borderRadius: 20,
                                                background:
                                                    "linear-gradient(90deg, #4FAE22, #83CB55)",
                                                transition: "width .5s ease",
                                            }}
                                        />

                                    </Box>

                                </Box>

                            </>

                        );

                    })()}

                </Box>


                {/* ==================================================
                    COMPLAINTS
                ================================================== */}

                <Box
                    sx={{
                        background: theme.card,
                        border: `1px solid ${theme.border}`,
                        borderRadius: 3,
                        p: {
                            xs: 2,
                            md: 2.5,
                        },
                        boxShadow: theme.shadow,
                    }}
                >

                    {/* Complaints heading */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 2,
                            mb: 2,
                        }}
                    >

                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.2,
                            }}
                        >

                            <Box
                                sx={{
                                    width: 38,
                                    height: 38,
                                    borderRadius: 2,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    background: darkMode
                                        ? "rgba(15,109,171,.18)"
                                        : "#EAF3FA",
                                    color: COLORS.sky,
                                    fontSize: 18,
                                }}
                            >
                                <FileTextOutlined />
                            </Box>

                            <Box>

                                <Typography
                                    sx={{
                                        color: theme.text,
                                        fontWeight: 900,
                                        fontSize: 16,
                                    }}
                                >
                                    Complaints
                                </Typography>

                                <Typography
                                    sx={{
                                        color: theme.secondary,
                                        fontSize: 11,
                                    }}
                                >
                                    Student concerns and reported issues
                                </Typography>

                            </Box>

                        </Box>


                        <Box
                            sx={{
                                minWidth: 30,
                                height: 30,
                                px: 1,
                                borderRadius: 2,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: darkMode
                                    ? "rgba(246,201,68,.13)"
                                    : "#FFF7DF",
                                border: "1px solid rgba(246,201,68,.25)",
                                color: darkMode
                                    ? COLORS.goldLight
                                    : COLORS.orange,
                                fontSize: 12,
                                fontWeight: 900,
                            }}
                        >
                            {Array.isArray(selectedChild.complaints)
                                ? selectedChild.complaints.length
                                : 0}
                        </Box>

                    </Box>


                    {/* Complaints list */}
                    {Array.isArray(selectedChild.complaints) &&
                    selectedChild.complaints.length > 0 ? (

                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1.2,
                            }}
                        >

                            {selectedChild.complaints.map((item) => (

                                <Box
                                    key={item.id}
                                    sx={{
                                        p: 2,
                                        borderRadius: 2,
                                        background: theme.hover,
                                        border: `1px solid ${theme.border}`,
                                        borderLeft: "4px solid #CBA454",
                                        transition: "all .2s ease",
                                        "&:hover": {
                                            transform: "translateX(3px)",
                                            boxShadow: theme.shadow,
                                        },
                                    }}
                                >

                                    <Typography
                                        sx={{
                                            fontWeight: 850,
                                            fontSize: 13,
                                            color: theme.text,
                                            mb: 0.6,
                                        }}
                                    >
                                        {item.title || "Student Complaint"}
                                    </Typography>

                                    <Typography
                                        sx={{
                                            color: theme.secondary,
                                            fontSize: 12,
                                            lineHeight: 1.7,
                                            whiteSpace: "pre-wrap",
                                            overflowWrap: "anywhere",
                                        }}
                                    >
                                        {item.description || "No additional details provided."}
                                    </Typography>

                                </Box>

                            ))}

                        </Box>

                    ) : (

                        /* Empty complaints state */
                        <Box
                            sx={{
                                py: 3.5,
                                px: 2,
                                textAlign: "center",
                                borderRadius: 2.5,
                                background: theme.hover,
                                border: `1px dashed ${theme.border}`,
                            }}
                        >

                            <Box
                                sx={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: "50%",
                                    mx: "auto",
                                    mb: 1.2,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    background: darkMode
                                        ? "rgba(79,174,34,.13)"
                                        : "#EFF9EA",
                                    color: COLORS.green,
                                    fontSize: 20,
                                }}
                            >
                                <ReadOutlined />
                            </Box>

                            <Typography
                                sx={{
                                    color: theme.text,
                                    fontSize: 13,
                                    fontWeight: 850,
                                }}
                            >
                                No Complaints Found
                            </Typography>

                            <Typography
                                sx={{
                                    color: theme.secondary,
                                    fontSize: 11,
                                    mt: 0.5,
                                }}
                            >
                                There are currently no complaints recorded for this student.
                            </Typography>

                        </Box>

                    )}

                </Box>

            </>

        )}

    </Box>

)}


            </motion.div>

          </Content>

        </Layout>

      </Layout>

    </div>

  );

};

export default StudentDashboard;
